// Напускането на листа „Изписване" — общото `useWorksheetLeave`. Без промени
// — тихо. Ново изписване: черновата се пази сама (браузър + сървър) → излизаме
// тихо, като я записваме веднага; продължава се от началния екран. Редакция:
// питаме — въпросът назовава документа („редакцията на изписване №12") и
// показва попълнените редове (`writeoffPreviewItems` — същите са и във
// въпросите за черновата).
import type { MutableRefObject } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import type { Product } from "../../../../api/inventory";
import { formatQtyUnit } from "../../components/confirm/confirmPreviews";
import type { ConfirmPreviewItem } from "../../components/confirm/confirmTypes";
import { useWorksheetLeave } from "../../components/drafts/useWorksheetLeave";
import { portionsLabel } from "./writeoffConfirms";
import { parseDecimal, type WriteoffKind, type WriteoffLineDraft } from "./writeoffWorksheetShared";

// „Продукт — количество" / „Рецепта — 2 порции" на попълнените редове
export const writeoffPreviewItems = (
  t: TFunction,
  lines: WriteoffLineDraft[],
  productById: Map<number, Product>,
): ConfirmPreviewItem[] =>
  lines
    .filter((line) => parseDecimal(line.quantity) > 0)
    .map((line) => {
      const quantity = parseDecimal(line.quantity);
      if (line.kind === "recipe")
        return { key: line.key, label: line.recipeName ?? "—", value: portionsLabel(t, quantity) };
      const product = line.productId != null ? productById.get(line.productId) : undefined;
      return {
        key: line.key,
        label: product?.name ?? `#${line.productId}`,
        value: formatQtyUnit(quantity, product?.decimalPlaces, product?.unit),
      };
    });

type Params = {
  mode: "new" | "edit";
  kind: WriteoffKind;
  lines: WriteoffLineDraft[];
  productById: Map<number, Product>;
  dirtyRef: MutableRefObject<boolean>;
  // нов документ: черновата се записва веднага и листът излиза тихо
  flushDraft?: () => Promise<void>;
  onRegisterLeaveGuard?: (guard: ((proceed: () => void) => void) | null) => void;
};

export const useWriteoffLeave = ({
  mode,
  kind,
  lines,
  productById,
  dirtyRef,
  flushDraft,
  onRegisterLeaveGuard,
}: Params) => {
  const { t } = useTranslation();
  const { writeoffId } = useParams();
  const scrap = kind === "scrap";
  return useWorksheetLeave({
    mode,
    dirtyRef,
    flushDraft,
    onRegisterLeaveGuard,
    describeUnsaved: () => ({
      document:
        mode === "new"
          ? t(scrap ? "inventoryPage.cfmDocNewScrap" : "inventoryPage.cfmDocNewWriteoff")
          : t(scrap ? "inventoryPage.cfmDocEditScrap" : "inventoryPage.cfmDocEditWriteoff", {
              number: writeoffId ?? "",
            }),
      isNew: mode === "new",
      items: writeoffPreviewItems(t, lines, productById),
    }),
  });
};
